import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { IconCamera, IconCheck, IconFileTypePdf, IconId, IconUpload } from "@tabler/icons-react";
import { AgentHeardFrom, AgentStatus, TANZANIA_REGIONS, TANZANIA_REGION_NAMES, AGENT_UPLOAD_MAX_BYTES } from "@safiri/shared";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { Me } from "../lib/types";
import { formatDate, fromApiDate, toApiDate } from "../lib/format";
import { useI18n, type TKey } from "../i18n";
import { normalizePhone } from "./AuthPages";
import { Alert, Badge, Button, Card, Checkbox, cx, ErrorAlert, Input, PageTitle, PhoneInput, Progress, Select, useAction, useToast } from "../components/ui";

const HEARD: Record<AgentHeardFrom, TKey> = {
  [AgentHeardFrom.SAFIRI_STAFF]: "heardSafiriStaff",
  [AgentHeardFrom.ANOTHER_AGENT]: "heardAnotherAgent",
  [AgentHeardFrom.SOCIAL_MEDIA]: "heardSocialMedia",
  [AgentHeardFrom.OTHER]: "heardOther",
};

/** Every field the backend can list in `missing` (agent.service.ts). */
const REQUIRED = ["name", "phoneVerified", "dob", "gender", "region", "district", "street", "idDocument", "profilePhoto"];

const maxDob = () => new Date(Date.now() - 18 * 365.25 * 86400_000).toISOString().slice(0, 10);

function Upload({
  kind,
  label,
  hint,
  accept,
  current,
  disabled,
  round,
  onDone,
}: {
  kind: "idDocument" | "profilePhoto";
  label: string;
  hint: string;
  accept: string;
  current: string | null;
  disabled: boolean;
  round?: boolean;
  onDone: (me: Me) => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const ref = useRef<HTMLInputElement>(null);
  const [tooBig, setTooBig] = useState(false);
  const up = useAction(async (file: File) => {
    await api.upload(`/agent/me/uploads/${kind}`, file);
    onDone(await api.get<Me>("/agent/me"));
    toast(t("uploadDone"));
  });
  const isPdf = current ? /\.pdf(\?|$)/i.test(current) : false;
  const Empty = kind === "idDocument" ? IconId : IconCamera;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-ink">{label}</p>
        {current && (
          <Badge tone="green">
            <IconCheck size={12} /> {t("uploaded")}
          </Badge>
        )}
      </div>
      <button
        type="button"
        disabled={disabled || up.loading}
        onClick={() => ref.current?.click()}
        className={cx(
          "group relative flex items-center justify-center overflow-hidden border-2 border-dashed transition",
          round ? "mx-auto h-32 w-32 rounded-full" : "h-40 w-full rounded-2xl",
          current ? "border-transparent bg-sunken" : "border-line-strong bg-sunken/60 hover:border-primary/50",
          "disabled:cursor-default"
        )}
        aria-label={current ? `${t("replace")}: ${label}` : `${t("upload")}: ${label}`}
      >
        {current && !isPdf ? (
          <img src={current} alt="" className="h-full w-full object-cover" />
        ) : current ? (
          <span className="flex flex-col items-center gap-1 text-muted">
            <IconFileTypePdf size={40} />
            <span className="text-xs font-medium">PDF</span>
          </span>
        ) : (
          <span className="flex flex-col items-center gap-1.5 px-3 text-center text-muted">
            <Empty size={30} />
            <span className="text-xs font-medium">{t("tapToUpload")}</span>
          </span>
        )}
        {up.loading && (
          <span className="absolute inset-0 flex items-center justify-center bg-surface/70">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </span>
        )}
        {current && !disabled && !up.loading && (
          <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-black/55 py-1.5 text-xs font-medium text-white opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
            <IconUpload size={14} /> {t("replace")}
          </span>
        )}
      </button>
      {current && !disabled && (
        <Button variant="secondary" size="sm" className="sm:hidden" loading={up.loading} onClick={() => ref.current?.click()}>
          <IconUpload size={16} /> {t("replace")}
        </Button>
      )}
      {current && isPdf && (
        <a href={current} target="_blank" rel="noreferrer" className="text-center text-xs font-medium text-brand underline">
          {t("openFile")}
        </a>
      )}
      <p className="text-xs text-subtle">{hint}</p>
      {tooBig && <Alert tone="error">{t("fileTooBig")}</Alert>}
      <ErrorAlert error={up.error} />
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          setTooBig(file.size > AGENT_UPLOAD_MAX_BYTES);
          if (file.size <= AGENT_UPLOAD_MAX_BYTES) void up.run(file);
        }}
      />
    </div>
  );
}

function Step({ n, title, done, optional, children }: { n: number; title: string; done?: boolean; optional?: boolean; children: ReactNode }) {
  const { t } = useI18n();
  return (
    <Card as="section">
      <div className="mb-4 flex items-center gap-3">
        <span className={cx("flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold", done ? "bg-success text-white dark:text-canvas" : "bg-sunken text-muted")}>
          {done ? <IconCheck size={14} stroke={3} /> : n}
        </span>
        <h2 className="font-semibold text-ink">
          {title}
          {optional && <span className="ml-1.5 text-sm font-normal text-subtle">({t("optional")})</span>}
        </h2>
      </div>
      {children}
    </Card>
  );
}

/** Spec §5.1 steps 2–5 and §5.3: profile, terms, submit, then the status. */
export function ApplicationPage() {
  const { t, pick, lang } = useI18n();
  const { me, refresh } = useAuth();
  const toast = useToast();
  const [m, setM] = useState<Me | null>(me);
  const blank = { dob: "", gender: "", region: "", district: "", street: "", altPhoneNumber: "", heardFrom: "", referringStaffName: "", name: "" };
  const [f, setF] = useState(blank);
  const [base, setBase] = useState(blank);
  const [terms, setTerms] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    setM(me);
  }, [me]);
  useEffect(() => {
    if (!m) return;
    const next = {
      name: m.name,
      dob: fromApiDate(m.dob),
      gender: m.gender ?? "",
      region: m.region ?? "",
      district: m.district ?? "",
      street: m.street ?? "",
      altPhoneNumber: m.altPhoneNumber ?? "",
      heardFrom: m.heardFrom ?? "",
      referringStaffName: m.referringStaffName ?? "",
    };
    setF(next);
    setBase(next);
    // Reset the form when the account or its status changes, not on every refresh.
  }, [m?.id, m?.status]);

  const dirty = useMemo(() => JSON.stringify(f) !== JSON.stringify(base), [f, base]);

  // Plain function (not useAction) so submit can await it and stop if it fails.
  const persist = async () => {
    const next = await api.patch<Me>("/agent/me", {
      name: f.name.trim(),
      ...(f.dob ? { dob: toApiDate(f.dob) } : {}),
      ...(f.gender ? { gender: f.gender } : {}),
      ...(f.region ? { region: f.region } : {}),
      ...(f.district ? { district: f.district } : {}),
      ...(f.street.trim() ? { street: f.street.trim() } : {}),
      altPhoneNumber: f.altPhoneNumber.trim() ? normalizePhone(f.altPhoneNumber) : null,
      heardFrom: f.heardFrom || null,
      referringStaffName: f.referringStaffName.trim() || null,
    });
    setM(next);
    setBase(f);
    return next;
  };
  const save = useAction(async () => {
    await persist();
    toast(t("saved"));
  });
  const submit = useAction(async () => {
    if (dirty) await persist();
    const next = await api.post<Me>("/agent/me/submit", { acceptTerms: terms, acceptConsent: consent });
    setM(next);
    await refresh();
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast(t("submitted"));
  });

  if (!m) return null;
  const editable = m.canEdit;
  const missing = new Set(m.missing.map((x) => x.field));
  const doneCount = REQUIRED.filter((k) => !missing.has(k)).length;
  const pct = (doneCount / REQUIRED.length) * 100;
  const has = (...keys: string[]) => keys.every((k) => !missing.has(k));

  const hero =
    m.status === AgentStatus.UNDER_REVIEW ? (
      <Card className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
        <img src="/img/under-review.png" alt="" className="h-24 w-auto rounded-xl" />
        <div>
          <Badge status="underReview">{t("as_underReview")}</Badge>
          <p className="mt-2 font-semibold text-ink">{t("reviewTitle")}</p>
          <p className="mt-1 text-sm text-muted">{t("statusUnderReview")}</p>
          {m.submittedAt && <p className="mt-2 text-xs text-subtle">{t("submittedOn", { date: formatDate(m.submittedAt, lang) })}</p>}
        </div>
      </Card>
    ) : m.status === AgentStatus.REJECTED ? (
      <Alert tone="error" title={t("statusRejected")}>
        <p className="font-medium">{t("rejectedFor")}:</p>
        <ul className="ml-5 mt-1 list-disc">
          {m.rejection?.reasons.map((r) => <li key={r.code}>{pick(r.label)}</li>)}
          {m.rejection?.comment && <li>{m.rejection.comment}</li>}
        </ul>
      </Alert>
    ) : (
      <Card>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-ink">{t("profileProgress", { done: doneCount, total: REQUIRED.length })}</p>
            <p className="mt-0.5 text-sm text-muted">{t("statusDraft")}</p>
          </div>
          <span className="tabular text-2xl font-semibold text-brand">{Math.round(pct)}%</span>
        </div>
        <div className="mt-3">
          <Progress value={pct} label={t("profileProgress", { done: doneCount, total: REQUIRED.length })} tone={pct === 100 ? "success" : "primary"} />
        </div>
      </Card>
    );

  return (
    <div className={editable ? "pb-28 sm:pb-0" : ""}>
      <PageTitle subtitle={t("hello", { name: m.name.split(" ")[0] })}>{t("applicationTitle")}</PageTitle>
      <div className="flex flex-col gap-4">
        {hero}

        <Step n={1} title={t("personal")} done={has("name", "dob", "gender")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Input label={t("fullName")} hint={t("fullNameHint")} value={f.name} disabled={!editable} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={100} />
            </div>
            <Input label={t("dob")} type="date" value={f.dob} disabled={!editable} max={maxDob()} hint={t("dobHint")} onChange={(e) => setF({ ...f, dob: e.target.value })} />
            <Select
              label={t("gender")}
              value={f.gender}
              disabled={!editable}
              placeholder={t("choose")}
              onChange={(e) => setF({ ...f, gender: e.target.value })}
              options={[
                { value: "female", label: t("female") },
                { value: "male", label: t("male") },
              ]}
            />
          </div>
        </Step>

        <Step n={2} title={t("location")} done={has("region", "district", "street")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Select label={t("region")} value={f.region} disabled={!editable} placeholder={t("choose")} onChange={(e) => setF({ ...f, region: e.target.value, district: "" })} options={TANZANIA_REGION_NAMES.map((r) => ({ value: r, label: r }))} />
            <Select
              label={t("district")}
              value={f.district}
              disabled={!editable || !f.region}
              placeholder={f.region ? t("choose") : t("pickRegionFirst")}
              onChange={(e) => setF({ ...f, district: e.target.value })}
              options={(TANZANIA_REGIONS[f.region] ?? []).map((d) => ({ value: d, label: d }))}
            />
            <div className="sm:col-span-2">
              <Input label={t("street")} value={f.street} disabled={!editable} onChange={(e) => setF({ ...f, street: e.target.value })} maxLength={200} />
            </div>
          </div>
        </Step>

        <Step n={3} title={t("identity")} done={has("idDocument", "profilePhoto")}>
          <div className="grid gap-6 sm:grid-cols-2">
            <Upload kind="profilePhoto" label={t("profilePhoto")} hint={t("profilePhotoHint")} accept="image/jpeg,image/png" current={m.profilePhotoUrl} disabled={!editable} round onDone={setM} />
            <Upload kind="idDocument" label={t("idDocument")} hint={t("idDocumentHint")} accept="image/jpeg,image/png,application/pdf" current={m.idDocumentUrl} disabled={!editable} onDone={setM} />
          </div>
        </Step>

        <Step n={4} title={t("more")} optional>
          <div className="grid gap-4 sm:grid-cols-2">
            <PhoneInput label={t("altPhone")} value={f.altPhoneNumber} disabled={!editable} onChange={(e) => setF({ ...f, altPhoneNumber: e.target.value })} optional />
            <Select label={t("heardFrom")} value={f.heardFrom} disabled={!editable} placeholder={t("choose")} onChange={(e) => setF({ ...f, heardFrom: e.target.value })} options={Object.values(AgentHeardFrom).map((h) => ({ value: h, label: t(HEARD[h]) }))} optional />
            <div className="sm:col-span-2">
              <Input label={t("referringStaff")} hint={t("referringStaffHint")} value={f.referringStaffName} disabled={!editable} onChange={(e) => setF({ ...f, referringStaffName: e.target.value })} maxLength={100} optional />
            </div>
          </div>
        </Step>

        {editable && (
          <Step n={5} title={t("agreeAndSubmit")} done={m.missing.length === 0 && terms && consent}>
            {m.missing.length > 0 && (
              <div className="mb-4 rounded-xl bg-warning-soft p-3.5">
                <p className="text-sm font-medium text-ink">{t("stillMissing")}:</p>
                <ul className="mt-1.5 flex flex-wrap gap-1.5">
                  {m.missing.map((x) => (
                    <li key={x.field} className="rounded-full bg-surface px-2.5 py-1 text-xs font-medium text-ink">
                      {t(`miss_${x.field}` as TKey)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Checkbox checked={terms} onChange={setTerms}>
                {t("acceptTerms")} ·{" "}
                <Link to="/terms" target="_blank" className="font-medium text-brand underline">
                  {t("readTerms")}
                </Link>
              </Checkbox>
              <Checkbox checked={consent} onChange={setConsent}>
                {t("acceptConsent")}
              </Checkbox>
            </div>
            <div className="mt-4">
              <ErrorAlert error={save.error ?? submit.error} />
            </div>
            {/* Desktop actions; phones get the sticky bar below. */}
            <div className="mt-4 hidden gap-2 sm:flex">
              <Button variant="secondary" loading={save.loading} disabled={!dirty} onClick={() => void save.run()}>
                {dirty ? t("save") : t("saved")}
              </Button>
              <Button loading={submit.loading} disabled={!terms || !consent || (!m.canSubmit && !dirty)} onClick={() => void submit.run()}>
                {t("submit")}
              </Button>
            </div>
          </Step>
        )}
      </div>

      {editable && (
        <div className="pb-safe fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/95 px-4 pt-3 backdrop-blur-md sm:hidden">
          <div className="flex gap-2 pb-3">
            <Button variant="secondary" className="flex-1" loading={save.loading} disabled={!dirty} onClick={() => void save.run()}>
              {dirty ? t("save") : t("saved")}
            </Button>
            <Button className="flex-[1.4]" loading={submit.loading} disabled={!terms || !consent || (!m.canSubmit && !dirty)} onClick={() => void submit.run()}>
              {t("submit")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
